import activityReducer, { ACTIVITY_ACTION_TYPES } from '../context/activity.js';
import alertReducer, { ALERT_ACTION_TYPES } from '../context/alert.js';
import ajaxReducer, { AJAX_ACTION_TYPES } from '../context/ajax.js';
import projectFilterReducer, { blankFilter, PROJECT_FILTER_ACTION_TYPES } from '../context/projectFilter.js';
import projectLayoutReducer, { PROJECT_LAYOUT_ACTION_TYPES, withValidLayoutGrouping } from '../context/projectLayout.js';
import { filterProjectActivities } from '../util/FilterLogic.jsx';
import { persistOpenDatasets, readStoredOpenDatasets } from '../context/openDatasets.js';

const uniqueIds = ids => [...new Set((ids || []).map(id => String(id)).filter(Boolean))];

const withOpenDatasets = (state, openDatasets) => {
	const next = uniqueIds(openDatasets);
	persistOpenDatasets(next);
	return {
		...state,
		openDatasets: next
	};
};

export const initialState = {
	ajaxRequests: {},
	alert: null,
	datasets: {},
	tokenExpired: false,
	loginSucceeded: false,
	emailAddress: '',
	isSuperAdmin: false,
	mustChangePassword: false,
	createDate: null,
	currentDataset: null,
	openDatasets: readStoredOpenDatasets(),
	redirectURL: null,
	projectFilter: JSON.parse(JSON.stringify(blankFilter)),
	layoutChanged: false,
	layoutChangedModalInfo: null,
	wbsData: null,
	projectActivities: null,
	projectActivitiesMap: null,
	projectActivityCodesRelativesMap: null,
	projectActivityCodesTree: null,
	projectActivityCodesTreeSequence: null,
	projectLayouts: [],
	projectLayoutId: null,
	projectViewType: 'WBS List',
	projectGroupBy: null,
	projectGroupByActivityCode: null,
	projectMapGroupBy: null,
	tourInfo: null,
	isBulkActive: false,
	bulkActivities: [],
	filteredActivities: [],
	filteredActivitiesMap: {},
	isOnline: navigator.onLine,
	showAddProjectModal: false
};

export const reducer = (state = initialState, action) => {
	const { type, payload } = action;
	if (type in PROJECT_FILTER_ACTION_TYPES) {
		return projectFilterReducer(state, action);
	}
	if (type in PROJECT_LAYOUT_ACTION_TYPES) {
		return projectLayoutReducer(state, action);
	}
	if (type in ACTIVITY_ACTION_TYPES) {
		return activityReducer(state, action);
	}
	if (type in ALERT_ACTION_TYPES) {
		return alertReducer(state, action);
	}
	if (type in AJAX_ACTION_TYPES) {
		return ajaxReducer(state, action);
	}
	switch (type) {
		case 'SET_DATASETS': {
			const datasetIds = new Set(Object.keys(action.datasets || {}));
			const newState = withOpenDatasets(state, state.openDatasets.filter(id => datasetIds.has(String(id))));
			newState.datasets = action.datasets;
			return {
				...newState,
				...filterProjectActivities(newState)
			};
		}
		case 'OPEN_DATASET': {
			if (!action.datasetId) {
				return state;
			}
			return withOpenDatasets(state, [action.datasetId]);
		}
		case 'CLOSE_DATASET': {
			if (!action.datasetId) {
				return state;
			}
			return withOpenDatasets(
				state,
				state.openDatasets.filter(id => id !== String(action.datasetId))
			);
		}
		case 'SET_DATASET_INFO': {
			const layouts = Array.isArray(payload.projectLayouts) ? payload.projectLayouts : [];
			const layout = layouts.find(layout => layout.Id === payload.projectLayoutId);
			const projectFilter = layout && layout.Filter && typeof layout.Filter === 'object'
				? {...layout.Filter}
				: JSON.parse(JSON.stringify(blankFilter));
			const grouping = withValidLayoutGrouping({
				ViewType: payload.projectViewType,
				GroupBy: payload.projectGroupBy,
				GroupByActivityCode: payload.projectGroupByActivityCode
			}, payload.projectActivityCodesTree);
			const focusedId = payload.currentDataset == null ? null : String(payload.currentDataset);
			const openDatasets = focusedId ? [focusedId] : [];
			if (focusedId) {
				persistOpenDatasets(openDatasets);
			}
			const newState = {
				...state,
				openDatasets,
				currentDataset: payload.currentDataset,
				wbsData: payload.wbsData,
				projectActivities: payload.projectActivities,
				projectActivitiesMap: payload.projectActivitiesMap,
				projectActivityCodesRelativesMap: payload.projectActivityCodesRelativesMap,
				projectActivityCodesTree: payload.projectActivityCodesTree,
				projectActivityCodesTreeSequence: payload.projectActivityCodesTreeSequence,
				projectLayouts: layouts,
				projectLayoutId: payload.projectLayoutId,
				projectViewType: grouping.ViewType,
				projectGroupBy: grouping.GroupBy,
				projectGroupByActivityCode: grouping.GroupByActivityCode,
				projectMapGroupBy: payload.projectMapGroupBy,
				projectFilter,
				layoutChanged: false
			};
			return {
				...newState,
				...filterProjectActivities(newState)
			};
		}
		case 'SET_TOKEN_EXPIRED':
			return {
				...state,
				tokenExpired: action.tokenExpired
			};
		case 'SET_LOGIN_SUCCEEDED':
			return {
				...state,
				loginSucceeded: action.loginSucceeded
			};
		case 'SET_EMAIL_ADDRESS':
			return {
				...state,
				emailAddress: action.emailAddress
			};
		case 'SET_IS_SUPER_ADMIN':
			return {
				...state,
				isSuperAdmin: Boolean(action.isSuperAdmin)
			};
		case 'SET_MUST_CHANGE_PASSWORD':
			return {
				...state,
				mustChangePassword: Boolean(action.mustChangePassword)
			};
		case 'SET_CREATE_DATE':
			return {
				...state,
				createDate: action.createDate
			};
		case 'SET_REDIRECT_URL':
			return {
				...state,
				redirectURL: action.redirectURL
			};
		case 'SET_PROJECT_ACTIVITIES': {
			const newState = {
				...state,
				projectActivities: action.projectActivities
			};
			return {
				...newState,
				...filterProjectActivities(newState)
			};
		}
		case 'SET_PROJECT_VIEW_TYPE':
			return {
				...state,
				layoutChanged: true,
				projectViewType: action.projectViewType
			};
		case 'SET_PROJECT_GROUP_BY':
			return {
				...state,
				layoutChanged: true,
				projectGroupBy: action.projectGroupBy
			};
		case 'SET_PROJECT_GROUP_BY_ACTIVITY_CODE':
			return {
				...state,
				layoutChanged: true,
				projectGroupByActivityCode: action.projectGroupByActivityCode
			};
		case 'SET_PROJECT_MAP_GROUP_BY':
			return {
				...state,
				layoutChanged: true,
				projectMapGroupBy: action.projectMapGroupBy
			};
		case 'SET_TOUR_INFO':
			// eslint-disable-next-line no-case-declarations
			let tourInfo = { ...state.tourInfo };
			if (Number.isInteger(action.tourInfo)) tourInfo[action.tourInfo] = 1;
			else tourInfo = action.tourInfo;
			return {
				...state,
				tourInfo
			};
		case 'SET_IS_BULK_ACTIVE':
			return {
				...state,
				isBulkActive: action.isBulkActive
			};
		case 'SET_BULK_ACTIVITIES':
			return {
				...state,
				bulkActivities: action.bulkActivities
			};
		case 'SET_IS_ONLINE':
			return {
				...state,
				isOnline: action.isOnline
			};
		case 'SET_SHOW_ADD_PROJECT_MODAL': {
			const { payload } = action;
			const { showAddProjectModal } = payload;
			return {
				...state,
				showAddProjectModal
			};
		}
		default:
			console.log('action type not found: ', type);
			return state;
	}
};
